import {PasswordUpdateBySecurityQuestionRequestDto} from './api';
import {Page} from 'Core/models/Page';
import {RoleId} from 'Core/models/Role';
import {SecurityQuestion} from 'Core/models/SecurityQuestion';
import {map as mapEither} from 'fp-ts/lib/Either';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {map as mapTaskEither} from 'fp-ts/lib/TaskEither';
import {ValidRegionCode} from 'Agent/Policies/models/RegionCode';
import {EncryptedTaxIdentificationNumber} from 'Agent/Policies/models/TaxIdentificationNumber';
import {getEitherValue} from 'Shared/components/ValidatablePhoneInput';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {get, getAxiosConfig, handleResponse, post, put, remove, withCancellation} from 'Shared/utilities/fetchHelpers';
import {parseUserClaims} from 'Shared/utilities/UserProfileUtilities';
import {ExternalValidateApi, InternalValidateApi, UserPasswordApi} from 'Users/api';
import {mapVisibilityPermission} from 'Users/api/agentApi';
import {UserEmailApi, UserMobilePhoneApi, UserApi, FieldSalesLeadersApi, ProfileApi} from 'Users/api/api';
import {UserFilter, UserType} from 'Users/ducks/userAdministration';
import {AllstateChannel} from 'Users/models/AllstateChannel';
import {ValidEnrollTrackUser} from 'Users/models/EnrollTrackUser';
import {ValidFieldSalesLeader} from 'Users/models/FieldSalesLeader';
import {ValidFieldVicePresident} from 'Users/models/FieldVicePresident';
import {ValidIndependentAgentRegistration} from 'Users/models/IndependentAgent';
import {IndependentAgentProfile} from 'Users/models/IndependentAgentProfile';
import {ValidPasswordResetForm} from 'Users/models/PasswordResetForm';
import {PreregisteredAgent, PreregisteredAgentFilter} from 'Users/models/PreregisteredAgent';
import {PreregisteredUserPayload} from 'Users/models/PreregisteredUserPayload';
import {SecurityQuestionPayload} from 'Users/models/SecurityQuestionPayload';
import {UserPayload} from 'Users/models/UserPayload';
import {ValidUserRegistration} from 'Users/models/UserRegistration';
import {ValidVip} from 'Users/models/Vip';
import axiosInstance from 'Shared/utilities/axiosInstance';
import {EnrollTrackRole} from 'Users/models/User/EnrollmentTrackingUserType';

const internalValidationAPI = new InternalValidateApi(undefined, process.env.REACT_APP_BASE_GATEWAY_URL, axiosInstance);
const externalValidationAPI = new ExternalValidateApi(undefined, process.env.REACT_APP_BASE_GATEWAY_URL, axiosInstance);
const emailValidationAPI = new UserEmailApi(undefined, process.env.REACT_APP_BASE_GATEWAY_URL, axiosInstance);
const mobileValidationAPi = new UserMobilePhoneApi(undefined, process.env.REACT_APP_BASE_GATEWAY_URL, axiosInstance);
const userPasswordApi = new UserPasswordApi(undefined, process.env.REACT_APP_BASE_GATEWAY_URL, axiosInstance);
const userApi = new UserApi(undefined, process.env.REACT_APP_BASE_GATEWAY_URL, axiosInstance);
const fieldSalesLeaderAPI = new FieldSalesLeadersApi(undefined, process.env.REACT_APP_BASE_GATEWAY_URL, axiosInstance);
const userProfileApi = new ProfileApi(undefined, process.env.REACT_APP_BASE_GATEWAY_URL, axiosInstance);

export const usersRootUri = '/users';

export default {
  deleteUser: (userId: string, applicationIds: number[], encryptedIdentifier?: string) =>
    userApi
      .userDelete(userId, '1', applicationIds, {
        ...getAxiosConfig(),
        params: {encryptedIdentifier},
      })
      .then(handleResponse),
  resetUserPassword: (userId: string) => userApi.userResetPassword(userId, '1', getAxiosConfig()).then(handleResponse),
  updatePassword: (
    userId: string,
    requestKey: string,
    independentAgentProfile: IndependentAgentProfile,
    passwordResetForm: ValidPasswordResetForm
  ) =>
    userPasswordApi
      .userPasswordUpdateBySecurityQuestions(userId, '1', {
        requestKey,
        password: passwordResetForm.password.passwordLine1,
        securityQuestions: [
          {
            id: independentAgentProfile.securityQuestions![0].id,
            question: independentAgentProfile.securityQuestions![0].question,
            response: passwordResetForm.answerOne.value,
          },
          {
            id: independentAgentProfile.securityQuestions![1].id,
            question: independentAgentProfile.securityQuestions![1].question,
            response: passwordResetForm.answerTwo.value,
          },
        ],
      })
      .then(handleResponse),
  getPasswordResetRequest: (key: string) =>
    userPasswordApi.userPasswordGetPasswordResetRequest(key, '1', getAxiosConfig()).then(handleResponse),
  requestPasswordReset: (username: string) =>
    userPasswordApi.userPasswordRequestReset(username, '1', getAxiosConfig()).then(handleResponse),
  getAvailableSalesLeaders: () =>
    fieldSalesLeaderAPI.fieldSalesLeadersGetAllAvailableFieldSalesLeaders('1', getAxiosConfig()).then(handleResponse),
  validateEmail: (email: string) =>
    withCancellation(cancelToken =>
      emailValidationAPI.userEmailValidate(email, '1', getAxiosConfig(cancelToken, false)).then(handleResponse)
    ),
  validatePhoneNumber: (phoneNumber: string) =>
    withCancellation(cancelToken =>
      mobileValidationAPi
        .userMobilePhoneValidate(phoneNumber, '1', getAxiosConfig(cancelToken, false))
        .then(handleResponse)
    ),
  validateExternalEmail: (email: string, registrationKey: string) =>
    withCancellation(cancelToken =>
      externalValidationAPI
        .externalValidateValidateEmail(registrationKey, email, '1', getAxiosConfig(cancelToken, false))
        .then(handleResponse)
    ),
  validateExternalPhoneNumber: (phoneNumber: string, registrationKey: string) =>
    withCancellation(cancelToken =>
      externalValidationAPI
        .externalValidateValidateMobilePhone(registrationKey, phoneNumber, '1', getAxiosConfig(cancelToken, false))
        .then(handleResponse)
    ),
  validateExternalUsername: (username: string, registrationKey: string) =>
    withCancellation(cancelToken =>
      externalValidationAPI
        .externalValidateValidateUserName(registrationKey, username, '1', getAxiosConfig(cancelToken, false))
        .then(handleResponse)
    ),
  validateAllstateUsername: (username: string) =>
    withCancellation(cancelToken =>
      internalValidationAPI
        .internalValidateValidateUserName(username, '1', getAxiosConfig(cancelToken, false))
        .then(handleResponse)
    ),
  stopImpersonation: (username: string) => put(`${usersRootUri}/${username}/impersonate/stop`),
  getUserProfile: (userId: string) =>
    withCancellation(cancelToken =>
      userProfileApi.profileGet(userId, '1', getAxiosConfig(cancelToken)).then(handleResponse)
    ),
  updatePasswordByUser: (
    userId: string,
    passwordUpdateRequest: PasswordUpdateBySecurityQuestionRequestDto,
    encryptedIdentifier: string
  ) =>
    withCancellation(cancelToken =>
      userProfileApi
        .profileUpdateBySecurityQuestions(userId, '1', passwordUpdateRequest, {
          ...getAxiosConfig(cancelToken),
          params: {encryptedIdentifier},
        })
        .then(handleResponse)
    ),
  saveEmail: (id: string, email: string, encryptedIdentifier?: string) =>
    put(`${usersRootUri}/${id}/email${addFilterParameters({encryptedIdentifier})}`, {
      email,
    }),
  saveName: (id: string, firstName: string, lastName: string, encryptedIdentifier?: string) =>
    put(`${usersRootUri}/${id}/name${addFilterParameters({encryptedIdentifier})}`, {
      firstName,
      lastName,
    }),
  saveSecurityQuestions: (
    id: string,
    securityQuestionOne: SecurityQuestion,
    securityQuestionTwo: SecurityQuestion,
    encryptedIdentifier?: string
  ) =>
    put(`${usersRootUri}/${id}/questions${addFilterParameters({encryptedIdentifier})}`, {
      securityQuestions: [
        {
          id: securityQuestionOne.question,
          response: securityQuestionOne.answer,
        },
        {
          id: securityQuestionTwo.question,
          response: securityQuestionTwo.answer,
        },
      ],
    }),
  saveMobilePhone: (id: string, mobilePhone: string, encryptedIdentifier?: string) =>
    put(`${usersRootUri}/${id}/mobilePhone${addFilterParameters({encryptedIdentifier})}`, {
      mobilePhone,
    }),
  validateFirstName: (firstName: string) => post(`${usersRootUri}/validate/firstName`, {firstName}),
  validateLastName: (lastName: string) => post(`${usersRootUri}/validate/lastName`, {lastName}),
  acceptTermsAndConditions: (username: string) => post(`${usersRootUri}/${username}/termsandconditions/accept`),
  deletePreregistration: (userId: string, applicationIds: number[], encryptedIdentifier?: string) =>
    remove(`${usersRootUri}/preregister/${userId}${addFilterParameters({encryptedIdentifier, applicationIds})}`),
  registerUser: (firstName: string, lastName: string, userKey: string, userRegistration: ValidUserRegistration) =>
    post(`${usersRootUri}/external`, {
      key: userKey,
      identityUser: {
        userName: userRegistration.userName.Data.right.value,
        password: userRegistration.password.passwordLine1,
        firstName,
        lastName,
        email: userRegistration.email.Data.right.address,
        mobilePhone: userRegistration.phoneNumber.Data.right.phoneNumber,
        securityQuestions: [
          {
            id: userRegistration.securityQuestionOne.question,
            response: userRegistration.securityQuestionOne.answer,
          },
          {
            id: userRegistration.securityQuestionTwo.question,
            response: userRegistration.securityQuestionTwo.answer,
          },
        ],
      },
    }),
  getSecurityQuestions: () => get<SecurityQuestionPayload[]>(`${usersRootUri}/questions`),
  getUserClaims: (username: string) =>
    get<any>(`${usersRootUri}/${username}/claims`).then(userClaimsEither =>
      pipe(userClaimsEither, mapEither(parseUserClaims))
    ),
  resendPreregistrationEmail: (userId: string, encryptedIdentifier?: string) =>
    post<unknown>(`${usersRootUri}/preregister/${userId}/resendemail${addFilterParameters({encryptedIdentifier})}`),
  linkAgentToAgency: (userName: string, userKey: string) =>
    post(`${usersRootUri}/preregister/link`, {
      key: userKey,
      userName,
    }),
  getPreregistrationUser: (preregistrationKey: string) =>
    get<PreregisteredUserPayload>(`${usersRootUri}/preregister/key/${preregistrationKey}`),
  preregisterAgent: (
    applicationIds: number[],
    registration: ValidIndependentAgentRegistration,
    encryptedIdentifier?: string
  ) =>
    post(`${usersRootUri}/external/preregister${addFilterParameters({encryptedIdentifier, applicationIds})}`, {
      agencyAssociations: fromAgencyAssociation(registration),
      channelId: AllstateChannel.BROKER_CHANNEL,
      email: registration.email.address,
      firstName: registration.firstName.value,
      lastName: registration.lastName.value,
    }),
  fetchEnrollTrackUser: (userId: string) => () => get<UserPayload>(`${usersRootUri}/${userId}/details`),
  getUser: (userId: string) => get<UserPayload>(`${usersRootUri}/${userId}/details`),
  getUsers: (filter: UserFilter) => get<Page<UserType>>(`${usersRootUri}${addFilterParameters(filter)}`),
  getRegionCodes: pipe(
    () => get<Array<{id: number; name: string}>>('/enums/regioncodes'),
    mapTaskEither(regions => regions.map(region => ValidRegionCode.create(region.name, region.id) as ValidRegionCode))
  ),
  saveFVPFieldSalesLeaders: (fvp: ValidFieldVicePresident, id: string) =>
    post(`${usersRootUri}/fieldvicepresident/${id}/fieldsalesleaders/assign`, fvp.fieldSalesLeaders),
  saveFieldVicePresidentUser: (fvp: ValidFieldVicePresident, id?: string) => {
    const fvpToSave = {
      id,
      firstName: fvp.firstName.value,
      lastName: fvp.lastName.value,
      email: fvp.email.Data.right.address,
      channelId: AllstateChannel.ALLSTATE_CHANNEL,
      mobilePhone: getEitherValue(fvp.phoneNumber.Data).phoneNumber,
      userName: fvp.username.Data.right.value,
      roleIds: [RoleId.FieldVicePresident],
      fieldSalesLeaders: fvp.fieldSalesLeaders,
    } as any;

    return id ? put('/users/agents/internal', fvpToSave) : post(`/users/agents/internal`, fvpToSave);
  },
  getPreregisteredUsers: (filter: PreregisteredAgentFilter, applicationIds: number[], encryptedIdentifier?: string) =>
    get<Page<PreregisteredAgent>>(
      `${usersRootUri}/preregister${addFilterParameters({encryptedIdentifier, applicationIds, ...filter})}`
    ),
  impersonate: (username: string, impersonatedUserName: string) =>
    put(`${usersRootUri}/${username}/impersonate/${impersonatedUserName}`),
  saveFieldSalesLeaderUser: (fsl: ValidFieldSalesLeader, id?: string) => {
    const fslToSave = {
      id,
      firstName: fsl.firstName.value,
      lastName: fsl.lastName.value,
      email: fsl.email.Data.right.address,
      channelId: AllstateChannel.ALLSTATE_CHANNEL,
      roleIds: [RoleId.FieldSalesLeader],
      mobilePhone: getEitherValue(fsl.phoneNumber.Data).phoneNumber,
      userName: fsl.username.Data.right.value,
      recruiterCode: {
        value: fsl.recruiterCode.right.code.value,
        isNewYork: fsl.isNewYork,
        isAhl: fsl.isAhl,
      },
      agencyAssociations: [],
      roles: [],
    };

    return id ? put('/users/agents/internal', fslToSave) : post(`/users/agents/internal`, fslToSave);
  },
  saveVipUser: (vip: ValidVip, id?: string) => {
    const vipToSave = {
      id,
      firstName: vip.firstName.value,
      channelId: AllstateChannel.ALLSTATE_CHANNEL,
      lastName: vip.lastName.value,
      roleIds: vip.isExternalAdministrator ? [RoleId.AllstateExternalAdministrator, RoleId.VIP] : [RoleId.VIP],
      email: vip.email.Data.right.address,
      mobilePhone: getEitherValue(vip.phoneNumber.Data).phoneNumber,
      userName: vip.username.Data.right.value,
      isAhl: vip.isAhl,
      isNewYork: vip.isNewYork,
    };

    return id ? put('/users/agents/internal', vipToSave) : post(`/users/agents/internal`, vipToSave);
  },
  saveEnrollTrackUser: (enrollTrackUser: ValidEnrollTrackUser, id: O.Option<string>) => {
    const enrollTrackUserToSave = {
      id,
      firstName: enrollTrackUser.firstName.value,
      lastName: enrollTrackUser.lastName.value,
      roleIds: enrollTrackUser.isUserSupport
        ? enrollTrackUser.roles.push(EnrollTrackRole.UserSupport.toString())
        : enrollTrackUser.roles,
      email: enrollTrackUser.email.Data.right.address,
      mobilePhone: getEitherValue(enrollTrackUser.phoneNumber.Data).phoneNumber,
      userName: enrollTrackUser.username.Data.right.value,
    };

    return O.isSome(id)
      ? put('/users/enrollTrack', enrollTrackUserToSave)
      : post(`/users/enrollTrack`, enrollTrackUserToSave);
  },
  updateDefaultAgencyAssociation: (id: string, encryptedIdentifier: string, isNewYork?: boolean) =>
    withCancellation(cancelToken =>
      userApi
        .updateDefaultAgencyAssociation(id, '1', encryptedIdentifier, isNewYork, getAxiosConfig(cancelToken, false))
        .then(handleResponse)
    ),
};

const fromAgencyAssociation = (registration: ValidIndependentAgentRegistration) =>
  registration.agencyAssociations.map(association => ({
    agentNumbers: association.agentNumbers.map(agentNumber => ({
      isNewYork: agentNumber.isNewYork,
      value: agentNumber.value,
    })),
    email: registration.email.address,
    [association.taxIdNumber instanceof EncryptedTaxIdentificationNumber
      ? 'encryptedIdentifier'
      : 'identifier']: association.taxIdNumber.value,
    roleIds:
      association.visibilityPermissions.length > 0
        ? association.visibilityPermissions.map(mapVisibilityPermission)
        : association.isAdministrator
        ? [RoleId.Producer, RoleId.AgencyAdministrator]
        : [RoleId.Producer],
  }));
